/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */

import annotationPlugin from "chartjs-plugin-annotation";
import { FC, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";

import Colors from "@constants/Colors";
import Loader from "@src/components/shared-containers/Loader";
import Chart from "chart.js/auto";
import {
  DEFAULTSALESVALUE,
  formatNumberWithComma,
  formatNumberWithCommaAndDollar,
  formatWithPercentage,
} from "@src/constants";
import { CustomChartProps } from "./type";

const CustomChart: FC<CustomChartProps> = ({
  loading,
  labels,
  firstDataset,
  secondDataset,
  targetPlanValue,
  maxTickLimit,
  type,
  firstColor,
  secondColor,
  annotationColor,
  hideDollar,
  showPercentage,
  showEmptyState,
  // targetTotalValue,
}) => {
  const ref = useRef();
  Chart.register(annotationPlugin);

  // Target Total Annotations for future use
  // const showTargetTotalAnnotations =
  //   (targetTotalValue !== null && annotationColor) || false;

  const showTargetPlanAnnotations =
    (targetPlanValue !== null && annotationColor) || false;

  const getFirstData = () => {
    if (showEmptyState) return DEFAULTSALESVALUE;
    if (firstDataset.length) return firstDataset;
    return DEFAULTSALESVALUE;
  };

  const options: any = {
    scales: {
      x: {
        border: {
          color: firstDataset
            ? Colors?.neutral_gray_400
            : Colors.neutral_gray_300,
          width: 1,
        },
        time: {
          unit: "months",
        },
        grid: {
          tickColor: Colors?.primarygray_400,
          lineWidth: 1,
          lineHeight: 2,
          drawOnChartArea: false,
          drawTicks: true,
          offset: false,
        },
        ticks: {
          visible: true,
          autoSkip: true,
          maxTicksLimit: maxTickLimit,
          source: "auto",
          callback(index: number) {
            return type === "bar" ? labels[index]?.charAt(0) : labels[index];
          },
        },
      },
      y: {
        afterFit(scale: any) {
          if (firstDataset?.length === 0 || showEmptyState) {
            scale.width = 0;
          }
        },
        ticks: {
          crossAlign: "far",
          callback(value: number) {
            if (showTargetPlanAnnotations) {
              return formatWithPercentage(value);
            }
            return new Intl.NumberFormat("en", {
              notation: "compact",
            }).format(value);
          },
          maxTicksLimit: 5,
        },
        border: {
          display: false,
        },
        beginAtZero: true,
        grid: {
          color: Colors.neutral_gray_300,
          drawTicks: false,
        },
      },
    },
    layout: {
      padding: 0,
    },
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label(context: any) {
            let label = "";
            if (hideDollar && context.raw !== null) {
              label += formatNumberWithComma(context.raw);
            } else if (showPercentage && context.raw !== null) {
              label += formatWithPercentage(context.raw);
            } else if (context.raw !== null) {
              label += formatNumberWithCommaAndDollar(context.raw);
            }
            return label;
          },
        },
      },
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      annotation: {
        annotations: {
          line1: showTargetPlanAnnotations && {
            type: "line",
            yMin: targetPlanValue,
            yMax: targetPlanValue,
            borderColor: annotationColor,
            borderWidth: 2,
            height: "1px",
            border: `0.2px solid ${annotationColor}`,
            drawTime: "beforeDatasetsDraw",
          },
          // total target annotation for future use
          // line2: showTargetTotalAnnotations && {
          //   type: "line",
          //   yMin: targetTotalValue,
          //   yMax: targetTotalValue,
          //   borderColor: Colors.success_500,
          //   borderWidth: 2,
          //   height: "1px",
          //   border: `0.2px solid ${Colors.success_500}`,
          //   drawTime: "beforeDatasetsDraw",
          // },
        },
      },
    },
  };

  if (loading) {
    return <Loader />;
  }

  if (type === "bar") {
    const data = {
      type,
      labels: showEmptyState ? [] : labels,
      datasets: [
        {
          backgroundColor: firstColor,
          borderWidth: 1,
          data: getFirstData(),
          stack: "stack1",
          maxBarThickness: 20,
          barPercentage: 0.5,
          offset: true,
        },
        {
          backgroundColor: secondColor,
          borderWidth: 1,
          data: secondDataset,
          stack: "stack1",
          maxBarThickness: 20,
          barPercentage: 0.5,
          offset: true,
        },
      ],
    };

    return <Bar ref={ref} data={data} options={options} />;
  }

  if (type === "line") {
    const data = {
      type,
      labels: showEmptyState ? [] : labels,
      datasets: [
        {
          backgroundColor: firstColor,
          borderColor: firstColor,
          borderWidth: 1.5,
          data: getFirstData(),
          stack: "stack1",
          fill: false,
          offset: true,
        },
        {
          backgroundColor: secondColor,
          borderColor: secondColor,
          borderWidth: 1.5,
          data: secondDataset,
          fill: false,
          stack: "stack1",
          offset: true,
        },
      ],
    };

    return <Line ref={ref} data={data} options={options} />;
  }
  const data = {
    type,
    labels: showEmptyState ? [] : labels,
    datasets: [
      {
        backgroundColor: firstColor,
        borderWidth: 1,
        data: getFirstData(),
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
      {
        backgroundColor: secondColor,
        borderWidth: 1,
        data: secondDataset,
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
    ],
  };

  return <Line ref={ref} data={data} options={options} />;
};

export default CustomChart;
